import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import PageHero from "@/components/PageHero";
import PageMeta from "@/components/PageMeta";
import SectionHeading from "@/components/SectionHeading";
import { IMAGES } from "@/lib/constants";
import {
  ArrowUpRight,
  HandHeart,
  Backpack,
  Users,
  UtensilsCrossed,
  ShieldAlert,
  Flame,
  Compass,
  Sparkles,
  GraduationCap,
  Heart,
} from "lucide-react";

const OUTREACH_PROGRAMS = [
  {
    icon: Backpack,
    t: "Annual Backpack Giveaway",
    b: "Each August, Phoenix supports students and families through a backpack giveaway before students return to school in September.",
  },
  {
    icon: UtensilsCrossed,
    t: "Senior Feeding & Fellowship",
    b: "Phoenix supports Detroit seniors through annual senior feeding and fellowship efforts, with additional support as resources and programming allow.",
  },
  {
    icon: HandHeart,
    t: "Families Impacted by Fire",
    b: "Phoenix collects clothing, essentials, and support items to assist families impacted by fire.",
  },
  {
    icon: GraduationCap,
    t: "Project 100 Mentorship",
    b: "Phoenix's recruitment and candidate-preparation initiative — mentorship, readiness, and community visibility for individuals pursuing the Detroit Fire Department pathway.",
  },
  {
    icon: Heart,
    t: "Retiree Fellowship",
    b: "Phoenix honors retired Detroit firefighters through ongoing fellowship, outreach, and connection — including the monthly Retirees Breakfast at Historic Engine 11.",
  },
  {
    icon: Users,
    t: "Active Member & Family Fellowship",
    b: "Phoenix is developing new fellowship opportunities for active Detroit Fire Department members across all divisions — Fire Operations, Emergency Medical Services, Fire Marshal, Fire Investigation, Training, Communications, Community Relations, and Apparatus — and their families. Retiree members are also dues-paying members and have their own dedicated fellowship circle.",
    developing: true,
  },
];

const PROJECT100_IS = [
  "Recruitment outreach to Detroit candidates interested in the fire service",
  "Mentorship from active Detroit firefighters and Phoenix members",
  "Preparation support — physical readiness, study sessions, fellowship",
  "Community visibility for the path into the Detroit Fire Department",
];

const PROJECT100_ISNOT = [
  "An official Detroit Fire Department program or academy",
  "A guarantee of hiring, employment, or placement",
  "A substitute for DFD testing, training, or hiring processes",
  "Authorized to make hiring decisions on behalf of the DFD",
];

export default function ProgramsOutreach() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    // Defer scroll until after layout/paint — DOM nodes for downstream
    // sections (e.g. #project-100) may not exist on the first synchronous
    // render. Two rAFs gives React time to commit the full tree, then we
    // scroll with the section anchor's own `scroll-mt-24` honored.
    const id = hash.replace(/^#/, "");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }, [hash]);

  return (
    <div data-testid="programs-outreach-page">
      <PageMeta
        title="Programs & Outreach"
        description="Phoenix outreach across Detroit — the annual backpack giveaway, senior feeding and fellowship, support for families impacted by fire, Project 100 preparation, and Phoenix fellowship initiatives."
        path="/programs-outreach"
      />
      <PageHero
        overline="Programs & Outreach"
        title="Detroit Firefighters Take Care of Detroit."
        subtitle="From the annual backpack giveaway to senior feeding and fellowship, support for families impacted by fire, Project 100 mentorship, and active and retiree fellowship for Phoenix members and their families — Phoenix shows up where it counts."
        image={IMAGES.outreach}
      >
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <Link to="/contact?type=volunteer" className="btn-gold w-full sm:w-auto justify-center" data-testid="po-hero-volunteer">
            Volunteer Interest <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link to="/contact?type=donation&subject=Fund%20a%20Phoenix%20Program" className="btn-primary w-full sm:w-auto justify-center" data-testid="po-hero-sponsor">
            Fund a Program
          </Link>
          <a href="#project-100" className="btn-ghost w-full sm:w-auto justify-center" data-testid="po-hero-project100">
            Project 100
          </a>
        </div>
      </PageHero>

      {/* ----- OUTREACH PROGRAMS ----- */}
      <section className="py-12 lg:py-28" data-testid="po-outreach">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Community Outreach"
            title="The work of the Phoenix in Detroit."
            subtitle="From the annual backpack giveaway to senior feeding and fellowship, support for families impacted by fire, Project 100 mentorship, active and retiree fellowship for Phoenix members and their families — Phoenix shows up where it counts."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {OUTREACH_PROGRAMS.map((p) => (
              <div key={p.t} className="card-dark p-7 flex flex-col" data-testid={`outreach-${p.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-start justify-between gap-3">
                  <p.icon className="h-7 w-7 text-amber-500" />
                  {p.developing && (
                    <span
                      className="text-[10px] tracking-[0.24em] uppercase font-accent text-slate-400 border border-slate-700 px-2 py-1 rounded"
                      data-testid={`outreach-${p.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-developing`}
                    >
                      Developing Initiative
                    </span>
                  )}
                </div>
                <div className="font-display text-xl text-white mt-5">{p.t}</div>
                <div className="text-slate-400 mt-3 text-sm leading-relaxed">{p.b}</div>
              </div>
            ))}
          </div>
          <p className="text-slate-500 text-xs leading-relaxed mt-10 max-w-3xl">
            Program availability, schedules, and reach depend on volunteer
            capacity, partner relationships, and seasonal planning. Phoenix
            will do its best to connect, provide, or refer based on available
            resources and program capacity as programming grows.
          </p>
        </div>
      </section>

      {/* ----- SIVAD JOHNSON SCHOLARSHIP ----- */}
      <section className="py-12 lg:py-24 bg-[#0E0E0E]" data-testid="po-scholarship">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="The Sivad Johnson Scholarship"
            title="Investing in Detroit's future."
            subtitle="Phoenix awards the Sivad Johnson Scholarship to a Detroit student pursuing higher education — honoring a member's legacy by opening the door wider for the next generation."
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="overflow-hidden rounded-2xl border border-slate-800 ring-1 ring-amber-500/20">
                <img
                  src="/photos/darrell-tucker-ii-2026.jpg?v=1"
                  alt="Darrell Tucker II, 2026 Sivad Johnson Scholarship recipient, receiving his scholarship check at Historic Engine 11 in front of the historic hose-reel carriage."
                  loading="eager"
                  className="w-full h-auto block"
                />
              </div>
              <p className="text-slate-500 text-xs leading-relaxed mt-3">
                <span className="text-amber-500 font-semibold">2026 Recipient · Darrell Tucker II</span> — receiving his Sivad Johnson Scholarship at Historic Engine 11, honoring sacrifice and igniting futures.
              </p>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              <p className="text-slate-300 leading-relaxed">
                The scholarship is part of Phoenix's broader commitment to mentorship and pathway building alongside Project 100 and is awarded annually to students whose work and intention reflect Phoenix values.
              </p>
              <div className="mt-8 space-y-4" data-testid="scholarship-recipients">
                <div className="overline text-amber-500">2026 Recipients</div>
                <div className="rounded-xl border border-slate-800 bg-black/40 p-3 flex items-center gap-4" data-testid="recipient-darrell-tucker-ii">
                  <img
                    src="/photos/cassie-marie-dunlap-2026.jpg?v=1"
                    alt="Darrell Tucker II — 2026 Sivad Johnson Scholarship recipient, headed to Howard University."
                    loading="lazy"
                    className="h-20 w-20 rounded-lg object-cover object-top flex-shrink-0 ring-1 ring-amber-500/20"
                  />
                  <div className="min-w-0">
                    <div className="font-display text-lg text-white leading-tight">Darrell Tucker II</div>
                    <div className="text-slate-400 text-sm mt-1">2026 Recipient</div>
                    <div className="text-slate-500 text-xs mt-1">Presented at Historic Engine 11</div>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-black/40 p-3 flex items-center gap-4" data-testid="recipient-cassie-marie-dunlap">
                  <img
                    src="/photos/darrell-tucker-ii-2026-portrait.jpg?v=1"
                    alt="Cassie Marie Dunlap — 2026 Sivad Johnson Scholarship recipient, headed to UNC Chapel Hill."
                    loading="lazy"
                    className="h-20 w-20 rounded-lg object-cover object-top flex-shrink-0 ring-1 ring-amber-500/20"
                  />
                  <div className="min-w-0">
                    <div className="font-display text-lg text-white leading-tight">Cassie Marie Dunlap</div>
                    <div className="text-slate-400 text-sm mt-1">2026 Recipient</div>
                    <div className="text-slate-500 text-xs mt-1">UNC Chapel Hill — Exercise &amp; Sport Science</div>
                  </div>
                </div>

                <div className="overline text-amber-500 pt-4">Recent Recipients</div>
                <div className="rounded-xl border border-slate-800 bg-black/40 p-3 flex items-center gap-4" data-testid="recipient-maria-t-brown">
                  <img
                    src="/photos/scholarship-presidents-wall.jpg?v=3"
                    alt="Maria T. Brown, 2025 Sivad Johnson Scholarship recipient, at Historic Engine 11."
                    loading="lazy"
                    className="h-20 w-20 rounded-lg object-cover object-top flex-shrink-0 ring-1 ring-slate-700"
                  />
                  <div className="min-w-0">
                    <div className="font-display text-lg text-white leading-tight">Maria T. Brown</div>
                    <div className="text-slate-400 text-sm mt-1">2025 Recipient</div>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-800 bg-black/40 p-3 flex items-center gap-4" data-testid="recipient-faith-moore">
                  <img
                    src="/photos/faith-moore-2024.jpg?v=1"
                    alt="Faith Moore, 2024 Sivad Johnson Scholarship recipient."
                    loading="lazy"
                    className="h-20 w-20 rounded-lg object-cover object-top flex-shrink-0 ring-1 ring-slate-700"
                  />
                  <div className="min-w-0">
                    <div className="font-display text-lg text-white leading-tight">Faith Moore</div>
                    <div className="text-slate-400 text-sm mt-1">2024 Recipient</div>
                  </div>
                </div>
              </div>
              <p className="text-slate-500 text-xs leading-relaxed mt-6">
                Recipients are recognized with permission. Application windows, eligibility, and award details are published as the next cycle is finalized.
              </p>
            </div>
          </div>

          {/* ----- Sivad Johnson Scholarship FLYER (restrained pre-launch surfacing) ----- */}
          <div className="mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start" data-testid="po-scholarship-flyer">
            <div className="lg:col-span-5 order-1">
              <div className="overline text-amber-500 mb-3">Official Scholarship Flyer</div>
              <h3 className="font-display text-2xl text-white leading-tight">
                Honoring sacrifice. Igniting futures.
              </h3>
              <p className="text-slate-300 leading-relaxed mt-4">
                The Sivad Johnson Scholarship honors sacrifice by investing in
                the next generation connected to Detroit fire service
                families.
              </p>
              <p className="text-slate-500 text-xs leading-relaxed mt-4">
                Application essay topic and submission window are announced
                when the scholarship period opens. Contact Phoenix for
                details.
              </p>
            </div>
            <div className="lg:col-span-7 order-2">
              <a
                href="/events/sivad-johnson-scholarship-flyer.jpg?v=5"
                target="_blank"
                rel="noreferrer"
                className="block overflow-hidden rounded-2xl border border-amber-500/30 hover:border-amber-500/60 transition-colors bg-black/40"
                data-testid="scholarship-flyer-link"
                aria-label="Open the Sivad Johnson Scholarship flyer in a new tab"
              >
                <img
                  src="/events/sivad-johnson-scholarship-flyer.jpg?v=5"
                  alt="Sivad Johnson Scholarship Program flyer — Honoring Sacrifice, Igniting Futures. Two $500 scholarships awarded annually to qualifying dependents of Detroit Firefighters sponsored by a Phoenix member."
                  loading="eager"
                  className="w-full h-auto block"
                />
              </a>
              <p className="text-slate-500 text-xs leading-relaxed mt-3">
                Tap or click the flyer to open the full-size version.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ----- ALSO FROM PHOENIX (LIGHT CALLOUT · TAILGATING + ENGINE 11 RENTALS) ----- */}
      <section className="pb-12 lg:pb-24" data-testid="po-also-from-phoenix">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl border border-slate-800 bg-black/40 px-6 py-6 lg:px-8 lg:py-7">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
              <div className="flex-1 min-w-0">
                <div className="overline text-slate-400">Also From Phoenix</div>
                <p className="text-slate-300 text-sm leading-relaxed mt-2">
                  Phoenix also hosts fellowship and tailgating activities, and
                  Historic Engine 11 is available for community gatherings,
                  weddings, and private events. Venue rental details live on
                  the Rent Historic Engine 11 page.
                </p>
              </div>
              <Link
                to="/rent-engine-11"
                className="btn-ghost w-full lg:w-auto justify-center"
                data-testid="po-also-rent-engine11"
              >
                Rent Historic Engine 11 <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ----- PROJECT 100 ----- */}
      <section id="project-100" className="py-12 lg:py-28 bg-[#0E0E0E] scroll-mt-24" data-testid="po-project100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-9">
              <SectionHeading
                overline="Initiative · Project 100"
                title="Preparation & mentorship for the path to the DFD."
                subtitle="Project 100 is a Phoenix-led preparation and mentorship initiative for individuals interested in pursuing the Detroit Fire Department pathway. It is not a Detroit Fire Department program, an academy, or a guarantee of employment."
              />
            </div>
            <div className="lg:col-span-3 flex justify-center lg:justify-end" data-testid="p100-logo">
              <img
                src="/logos/project-100.png"
                alt="Project 100 official logo — Phoenix maltese cross with rising flame."
                className="w-32 sm:w-36 lg:w-44 h-auto select-none"
                loading="lazy"
                draggable="false"
              />
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-dark p-8" data-testid="p100-is">
              <div className="overline flex items-center gap-2"><Flame className="h-4 w-4 text-amber-500" /> What Project 100 IS</div>
              <ul className="mt-5 space-y-3 text-slate-300 text-sm leading-relaxed">
                {PROJECT100_IS.map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-dark p-8" data-testid="p100-isnot">
              <div className="overline flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-amber-500" /> What Project 100 IS NOT</div>
              <ul className="mt-5 space-y-3 text-slate-300 text-sm leading-relaxed">
                {PROJECT100_ISNOT.map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 card-dark p-8 lg:p-10" data-testid="p100-disclaimer">
            <p className="text-slate-300 leading-relaxed">
              Hiring, testing, training, and employment decisions for the
              Detroit Fire Department remain solely with the Detroit Fire
              Department. Phoenix does not guarantee placement, hiring, or
              outcomes. Project 100 exists to prepare and support candidates
              in their personal pursuit of the DFD pathway.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/contact?type=project-100" className="btn-gold w-full justify-center" data-testid="p100-inquiry">
              Project 100 Inquiry <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/contact?type=volunteer&subject=Project%20100%20Mentor%20%2F%20Volunteer" className="btn-ghost w-full justify-center" data-testid="p100-volunteer">
              Mentor / Volunteer
            </Link>
          </div>
        </div>
      </section>

      {/* ----- HOW TO HELP ----- */}
      <section className="py-12 lg:py-28" data-testid="po-help">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Ways You Can Help"
            title="Stand with the Phoenix in Detroit."
            subtitle="Programs grow when supporters step in — through service, sponsorship, mentorship, and partnership."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card-dark p-7 flex flex-col" data-testid="help-volunteer">
              <Users className="h-7 w-7 text-amber-500" />
              <div className="font-display text-xl text-white mt-5">Volunteer</div>
              <p className="text-slate-400 mt-3 text-sm leading-relaxed flex-1">
                Give your time to community days, outreach events, mentorship, and seasonal programs.
              </p>
              <Link
                to="/contact?type=volunteer&subject=General%20Phoenix%20Volunteer%20Interest"
                className="btn-gold mt-6 w-full justify-center"
                data-testid="help-volunteer-cta-general"
              >
                General Phoenix Volunteer <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="card-dark p-7 flex flex-col" data-testid="help-sponsor">
              <Sparkles className="h-7 w-7 text-amber-500" />
              <div className="font-display text-xl text-white mt-5">Sponsor a Program</div>
              <p className="text-slate-400 mt-3 text-sm leading-relaxed flex-1">
                Underwrite Phoenix programs, including senior feeding and
                fellowship, the annual backpack giveaway, support for families
                impacted by fire, retiree fellowship, active member and family
                fellowship, and future firefighter mentorship.
              </p>
              <Link to="/contact?type=donation&subject=Sponsor%20a%20Phoenix%20Program" className="btn-primary mt-6 w-full justify-center" data-testid="help-sponsor-cta">
                Sponsorship Inquiry <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="card-dark p-7 flex flex-col" data-testid="help-partner">
              <Compass className="h-7 w-7 text-amber-500" />
              <div className="font-display text-xl text-white mt-5">Partner With Phoenix</div>
              <p className="text-slate-400 mt-3 text-sm leading-relaxed flex-1">
                Organizations, schools, businesses, and faith communities are invited to partner on outreach and service in Detroit.
              </p>
              <Link to="/contact?type=general" className="btn-ghost mt-6 w-full justify-center">
                Contact Phoenix <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
